import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent } from '@shopify/events';

// bc-mod: cap hover-driven prefetches across all quick-add cards on the
// page. Each card has its own component, so without a shared counter a
// customer dragging their mouse across a collection page could fire
// dozens of parallel product-page fetches. Click-path fetches bypass
// this cap — those are explicit intent, not speculation.
const MAX_CONCURRENT_PREFETCHES = 3;
let activePrefetchCount = 0;

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element | Promise<Element | null>>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();
  // bc-mod: hover-prefetch bookkeeping. See connectedCallback / #prefetchOnHover.
  /** @type {ReturnType<typeof setTimeout> | null} */
  #hoverPrefetchTimer = null;
  /** @type {AbortController | null} */
  #hoverListenersAbort = null;

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    if (productCard) return productCard.productPageUrl;

    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = hotspotProduct?.getHotspotProductLink();

    return productLink?.href || '';
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() ?? null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);

    // bc-mod: hover-prefetch the product page so the modal opens
    // instantly on click instead of paying the network round-trip after
    // the customer has already committed. Listener lives on the card /
    // hotspot wrapper because pointerenter doesn't bubble. 150ms debounce
    // keeps quick mouse traversals from triggering wasted fetches.
    const card = this.closest('product-card, product-hotspot-component');
    if (card) {
      this.#hoverListenersAbort = new AbortController();
      const { signal } = this.#hoverListenersAbort;
      card.addEventListener('pointerenter', this.#prefetchOnHover, { signal, passive: true });
      card.addEventListener('pointerleave', this.#cancelHoverPrefetch, { signal, passive: true });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    this.#hoverListenersAbort?.abort();
    this.#cancelHoverPrefetch();
    document.removeEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
  }

  #prefetchOnHover = (event) => {
    // Skip non-mouse pointers. pointerenter fires on touch *contact*
    // (not just hover), so a finger tap would otherwise schedule a
    // prefetch that races the click — wasting bandwidth and giving
    // touch users none of the speculative-load benefit because the
    // click arrives before the debounce timer fires anyway. Pen is
    // lumped with touch since pen-without-contact hover is unusual
    // for product browsing.
    if (event?.pointerType && event.pointerType !== 'mouse') return;
    if (this.#hoverPrefetchTimer) return;
    this.#hoverPrefetchTimer = setTimeout(() => {
      this.#hoverPrefetchTimer = null;
      const url = this.productPageUrl;
      if (!url) return;
      // Module-wide concurrency cap. If we already have the max number
      // of speculative fetches in flight, drop this one — the customer
      // will pay the click-path latency, but we won't saturate their
      // connection. Cache check first so a hot cache (the previous
      // hover finished, sat idle, customer is hovering again) still
      // resolves instantly.
      if (this.#cachedContent.has(url)) return;
      if (activePrefetchCount >= MAX_CONCURRENT_PREFETCHES) return;
      activePrefetchCount += 1;
      // Fire-and-forget; #ensureCached writes the result (or unwinds the
      // cache entry on failure) on its own. The .finally always runs so
      // the counter stays balanced even when fetch rejects.
      this.#ensureCached(url)
        .catch(() => {})
        .finally(() => {
          activePrefetchCount -= 1;
        });
    }, 150);
  };

  #cancelHoverPrefetch = () => {
    if (this.#hoverPrefetchTimer) {
      clearTimeout(this.#hoverPrefetchTimer);
      this.#hoverPrefetchTimer = null;
    }
  };

  /**
   * Returns the cached productGrid for `url`, fetching it if necessary.
   * Both the hover-prefetch and click paths funnel through here so a
   * click during an in-flight prefetch awaits the same promise instead
   * of kicking off a duplicate fetch (or, worse, aborting the prefetch
   * via #abortController).
   * @param {string} url
   * @returns {Promise<Element | null>}
   */
  async #ensureCached(url) {
    if (!url) return null;
    const cached = this.#cachedContent.get(url);
    if (cached) {
      try {
        return await cached;
      } catch (error) {
        if (this.#cachedContent.get(url) === cached) this.#cachedContent.delete(url);
        if (error?.name === 'AbortError') throw error;
        return null;
      }
    }
    const fetchPromise = (async () => {
      const html = await this.fetchProductPage(url, { rethrowAbort: true });
      if (!html) return null;
      const gridElement = html.querySelector('[data-product-grid-content]');
      return gridElement ? /** @type {Element} */ (gridElement.cloneNode(true)) : null;
    })();
    this.#cachedContent.set(url, fetchPromise);
    // Every cache write below is guarded on "our promise is still the live
    // entry" — #handleCartUpdate clears the whole cache mid-flight (the grid
    // bakes data-cart-quantity clamps), and an unguarded set() here would
    // resurrect that stale markup after the await.
    try {
      const productGrid = await fetchPromise;
      if (this.#cachedContent.get(url) === fetchPromise) {
        if (productGrid) {
          // Replace the in-flight promise with the resolved Element so
          // future cache hits skip the await tick.
          this.#cachedContent.set(url, productGrid);
        } else {
          this.#cachedContent.delete(url);
        }
      }
      return productGrid;
    } catch (error) {
      if (this.#cachedContent.get(url) === fetchPromise) this.#cachedContent.delete(url);
      // AbortError propagates so callers can tell "superseded/cancelled"
      // from "genuinely failed" (the click path must not navigate on abort).
      if (error?.name === 'AbortError') throw error;
      return null;
    }
  }

  /**
   * Updates quick-add button state when product variant is selected
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelectUpdate = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    if (this.dataset.usesSellingPlans === 'true') return;

    // Only flip choose <-> add when both buttons were rendered.
    // Otherwise the flip would hide the sole rendered button and reveal nothing.
    if (this.dataset.rendersBothButtons !== 'true') return;

    const productOptionsCount = this.dataset.productOptionsCount;
    let quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';

    // A single-option card can resolve to an unavailable variant (e.g. re-selecting a
    // sold-out swatch). Keep "Choose" so shoppers reach the picker, not a dead-end disabled "Add".
    if (quickAddButton === 'add' && this.#isSelectedVariantUnavailable()) {
      quickAddButton = 'choose';
    }

    this.setAttribute('data-quick-add-button', quickAddButton);
  };

  /**
   * Whether the card's currently selected swatch maps to an unavailable variant.
   * Reads `data-option-available` off the variant picker's selected option - the same
   * signal the product card uses. Only reports true on an explicit `false`, so an
   * unknown/absent signal leaves the caller's default ("add") untouched.
   * @returns {boolean}
   */
  #isSelectedVariantUnavailable() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.variantPicker?.selectedOption?.dataset.optionAvailable === 'false';
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;

    if (this.dataset.usesSellingPlans === 'true') {
      if (currentUrl) window.location.href = currentUrl;
      return;
    }

    // bc-mod: funneled through #ensureCached so a click during an
    // in-flight hover prefetch reuses the same fetch instead of starting
    // a fresh one.
    let productGrid;
    try {
      productGrid = await this.#ensureCached(currentUrl);
    } catch (_abortError) {
      // Aborted fetch: superseded by a newer request or the grid morphed
      // this card away mid-fetch. Not a failure — navigating would discard
      // the customer's filters/scroll for nothing. Let the newer action win.
      return;
    }

    if (!productGrid) {
      // bc-mod: fall back to a full-page navigation rather than opening
      // an empty modal. fetch failure means the customer would otherwise
      // see a blank dialog with no product info, no way to add to cart,
      // and no error indication — the PDP is a strictly better
      // destination than that.
      if (currentUrl) {
        window.location.assign(currentUrl);
      }
      return;
    }

    // Use a fresh clone from the cache
    const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
    await this.updateQuickAddModal(freshContent);
    this.#updateVariantPicker(productGrid);

    this.#openQuickAddModal();
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);
    this.#syncHeaderState(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  /**
   * Mirrors the header states that product media and details styles depend on. Those rules reach page
   * content from `#header-group` with a sibling combinator, which can't reach this body-level dialog.
   * Scroll is locked while the dialog is open, so the header can't change state until it closes.
   *
   * @param {QuickAddDialog} dialogComponent
   */
  #syncHeaderState(dialogComponent) {
    dialogComponent.toggleAttribute(
      'header-transparent',
      document.querySelector('header-component[transparent]') !== null
    );
    dialogComponent.toggleAttribute(
      'header-sticky-active',
      document.querySelector("#header-group #header-component[data-sticky-state='active']") !== null
    );
  }

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @param {{rethrowAbort?: boolean}} [options] - bc-mod: rethrowAbort lets
   *   #ensureCached distinguish a superseded fetch from a genuine failure;
   *   default (false) keeps the upstream null-on-abort contract for
   *   product-card's fire-and-forget warm-up calls.
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl, { rethrowAbort = false } = {}) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        if (rethrowAbort) throw error;
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    // bc-mod: clear modalContent before morph so each open starts fresh.
    // Custom elements like <bc-custom-length> reparent themselves (anchor
    // into <variant-picker>) on connect; the old, anchored copy from a
    // previous open ends up at a different position than the freshly
    // fetched server-rendered copy, so morph can't reconcile them and
    // duplicates accumulate on every reopen. Wiping first guarantees the
    // new copies are the only ones in the dialog.
    modalContent.replaceChildren();

    // bc-mod: give the modal's buy-buttons form its own id. Opened ON a
    // product page, the fetched form carries the same id as the page's own
    // (both are BuyButtons-ProductForm-<main section id>), so every
    // form="<id>" control in the modal — the sewn-loop radios, the custom
    // length / sale property inputs — resolved to the PAGE's form, and
    // Shopify's wallet buttons, which collect properties document-wide with
    // [name^="properties"][form^="<id>"], picked the page's controls up for
    // the modal's checkout. A "QuickAdd-" PREFIX (their selector is a prefix
    // match) keeps the two apart; references inside the fragment follow.
    for (const form of productGrid.querySelectorAll('form[id^="BuyButtons-ProductForm-"]')) {
      const formId = form.getAttribute('id') || '';
      const modalFormId = `QuickAdd-${formId}`;
      form.setAttribute('id', modalFormId);
      for (const el of productGrid.querySelectorAll('[form], [data-form-id]')) {
        if (el.getAttribute('form') === formId) el.setAttribute('form', modalFormId);
        if (el.getAttribute('data-form-id') === formId) el.setAttribute('data-form-id', modalFormId);
      }
    }

    // Sync the view-event-payload attribute and morph children into the modal's product-component
    const payload = productGrid.getAttribute('view-event-payload') || '';
    modalContent.setAttribute('view-event-payload', payload);

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(StandardEvents.cartLinesUpdate, this.handleCartUpdate, {
      signal: this.#abortController.signal,
    });
    this.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog on successful cart update
   * @param {CartLinesUpdateEvent} event - The cart lines update event
   */
  handleCartUpdate = (event) => {
    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;
        this.closeDialog();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  /** @param {ProductSelectEvent} event - The product select event */
  #handleProductSelect = (event) => {
    // Wait for variant update data
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const anchorElement = /** @type {HTMLAnchorElement} */ (html.querySelector('.view-product-title a'));
        const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
        const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

        if (!anchorElement) return;

        if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
        if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
